import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, KeyRound, Users, Truck } from "lucide-react";
import TopBar from "@/components/TopBar";
import FestivePattern from "@/components/FestivePattern";
import { useLanguage } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";

const features = [
  { key: "f1", Icon: ShieldCheck, tint: "#D81B8C" },
  { key: "f2", Icon: KeyRound, tint: "#3FA535" },
  { key: "f3", Icon: Users, tint: "#6B2C8F" },
  { key: "f4", Icon: Truck, tint: "#D81B8C" },
];

export default function Landing() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FFF9FB] overflow-x-hidden">
      {/* Hero with festive pattern background */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <FestivePattern />
        </div>
        {/* Soft gradient overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-[#FFF9FB]" />

        <div className="relative z-10">
          <TopBar hideBrand />

          <div className="max-w-3xl mx-auto px-5 sm:px-10 pt-6 sm:pt-10 pb-20 sm:pb-28 text-center">
          <div className="mt-10 sm:mt-14 flex justify-center">
              <span
                className="milk-shimmer-logo"
                style={{ "--shimmer-mask": "url(/milkado-logo.png)" }}
              >
                <img
                  src="/milkado-logo.png"
                  alt="MILKADO"
                  data-testid="hero-logo"
                  className="block w-[260px] sm:w-[360px] md:w-[420px] h-auto drop-shadow-[0_8px_24px_rgba(216,27,140,0.18)]"
                />
              </span>
            </div>

            <h1
              data-testid="hero-title"
              className="mt-10 sm:mt-12 font-display text-3xl sm:text-5xl font-black text-[#2A0E3C] leading-[1.05] tracking-tight"
            >
              {t("landing.heroTitle")}{" "}
              <span className="italic text-[#3FA535]">{t("landing.heroAccent")}</span>
            </h1>

            <p className="mt-5 sm:mt-6 text-[#5C4664] text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
              {t("landing.heroSub")}
            </p>

            <div className="mt-10 sm:mt-12 flex justify-center">
              <Button
                size="lg"
                onClick={() => navigate("/code")}
                data-testid="cta-guest"
                className="milk-shimmer milk-glow-pink rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white px-10 h-14 text-base font-semibold gap-2 active:scale-95 transition-transform"
              >
                {t("landing.ctaGuest")}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-5 sm:px-10 py-20 sm:py-28 max-w-6xl mx-auto relative">
        <div className="max-w-2xl mb-14 text-center mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D81B8C] mb-4">
            MILKADO
          </p>
          <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tight text-[#2A0E3C]">
            {t("landing.featuresTitle")}
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:gap-8">
          {features.map(({ key, Icon, tint }) => (
            <div
              key={key}
              data-testid={`feature-${key}`}
              className="bg-white rounded-2xl p-4 sm:p-7 border border-[#F2E2EC] hover:border-[#D81B8C]/40 hover:shadow-lg transition-all"
            >
              <div
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-3 sm:mb-5"
                style={{
                  backgroundColor: `${tint}15`,
                  color: tint,
                }}
              >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.2} />
              </div>
              <h3 className="font-display text-base sm:text-xl font-bold text-[#2A0E3C] mb-1.5 sm:mb-2 leading-tight">
                {t(`landing.${key}Title`)}
              </h3>
              <p className="text-xs sm:text-sm leading-relaxed text-[#5C4664]">
                {t(`landing.${key}Desc`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="px-5 sm:px-10 py-20 sm:py-28 bg-[#2A0E3C] text-white relative overflow-hidden">
        {/* Confetti dots on the dark section too */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `radial-gradient(circle, #D81B8C 1px, transparent 1px), radial-gradient(circle, #3FA535 1px, transparent 1px)`,
            backgroundSize: "60px 60px, 60px 60px",
            backgroundPosition: "0 0, 30px 30px",
          }}
        />
        <div className="max-w-6xl mx-auto relative">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#3FA535] mb-4">
            Process
          </p>
          <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tight mb-12 sm:mb-16">
            {t("landing.howItWorks")}
          </h2>
          <ol className="space-y-8 sm:space-y-10 max-w-3xl">
            {[1, 2, 3, 4].map((i) => (
              <li
                key={i}
                data-testid={`step-${i}`}
                className="flex items-start gap-5 sm:gap-7 pb-8 sm:pb-10 border-b border-white/10 last:border-0"
              >
                <div
                  className={`font-display text-5xl sm:text-7xl font-black leading-none w-16 shrink-0 ${
                    i % 2 === 0 ? "text-[#3FA535]" : "text-[#D81B8C]"
                  }`}
                >
                  {String(i).padStart(2, "0")}
                </div>
                <p className="text-lg sm:text-xl leading-snug text-white/90 pt-2">
                  {t(`landing.step${i}`)}
                </p>
              </li>
            ))}
          </ol>
          <div className="mt-14">
            <Button
              size="lg"
              onClick={() => navigate("/code")}
              data-testid="footer-cta-guest"
              className="milk-shimmer milk-glow-pink rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white px-10 h-14 font-semibold gap-2"
            >
              {t("landing.ctaGuest")}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      <footer className="px-5 sm:px-10 py-10 text-center text-xs text-[#5C4664]">
        © {new Date().getFullYear()} MILKADO
      </footer>
    </div>
  );
}
